/**
 * UmpiresWidget — Organized umpire crew display with deduplication
 * - Fetches from #umpires channel
 * - Deduplicates by game matchup (keeps latest assignment)
 * - Groups by game date
 * - Shows HP, 1B, 2B, 3B umpires in a clean grid
 * Refresh: 60s | CSS prefix: uw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchFromProxy, imageUrl, fetchUmpireScorecards, umpireProfileUrl } from '../lib/api'
import { findTeam } from '../lib/teamLogos'
import { RefreshCw, Scale, AlertCircle } from 'lucide-react'
import './UmpiresWidget.css'

/** Parse date from title like "Tuesday, March 24 . Umpire Crews" */
function parseDateFromTitle(title) {
  if (!title) return null
  const clean = title.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '').trim()
  const m = clean.match(/\w+,\s+(\w+)\s+(\d{1,2})/)
  if (!m) return null
  const months = { January: 0, February: 1, March: 2, April: 3, May: 4, June: 5, July: 6, August: 7, September: 8, October: 9, November: 10, December: 11 }
  const month = months[m[1]]
  if (month === undefined) return null
  const day = parseInt(m[2], 10)
  const now = new Date()
  let year = now.getFullYear()
  if (month < now.getMonth() - 6) year++
  return new Date(year, month, day)
}

/** Parse date from field value */
function parseDateFromFieldValue(value) {
  if (!value) return null
  const clean = value.replace(/[^\w\s:./]/g, ' ').trim()
  const m = clean.match(/\w{3}\s+(\w{3})\s+(\d{1,2})/)
  if (!m) return null
  const months = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }
  const month = months[m[1]]
  if (month === undefined) return null
  const day = parseInt(m[2], 10)
  const now = new Date()
  let year = now.getFullYear()
  if (month < now.getMonth() - 6) year++
  return new Date(year, month, day)
}

/** Parse time from field value */
function parseTimeFromValue(value) {
  if (!value) return null
  const clean = value.replace(/[^\w\s:./]/g, ' ').trim()
  const m = clean.match(/(\d{1,2}:\d{2}\s*(?:AM|PM)\s*ET)/i)
  return m ? m[1].trim() : null
}

/** Parse umpire positions from field value */
function parseUmpires(value) {
  if (!value) return {}
  const umps = {}
  // Two producer formats have to be handled, because the monitor emits the
  // second one and this parser only ever matched the first — so graphic posts
  // (which is all the umpire monitor posts now) yielded NO umpire names at all:
  //
  //   embed field  : "**HP:** Angel Hernandez"                 <- colon, one per line
  //   graphic body : "**HP** Angel Hernandez | **1B** ..."     <- no colon, pipe-separated
  //
  // Optional colon, and stop at a pipe or newline so the pipe-separated form
  // does not swallow the rest of the crew into one name.
  const patterns = [
    { key: 'HP', regex: /\*\*HP:?\*\*\s*([^|\n]+)/i },
    { key: '1B', regex: /\*\*1B:?\*\*\s*([^|\n]+)/i },
    { key: '2B', regex: /\*\*2B:?\*\*\s*([^|\n]+)/i },
    { key: '3B', regex: /\*\*3B:?\*\*\s*([^|\n]+)/i },
  ]
  for (const { key, regex } of patterns) {
    const m = value.match(regex)
    if (m) umps[key] = m[1].trim()
  }
  return umps
}

/** Parse matchup from field name like "LAA @ LAD" or content text */
function parseMatchupFromFieldName(name) {
  if (!name) return null
  const m = name.match(/(.+?)\s*[@]\s*(.+)/)
  if (m) return { awayText: m[1].trim(), homeText: m[2].trim() }
  return null
}

/** Parse matchup from content text like "**Los Angeles Angels @ Los Angeles Dodgers**" */
function parseMatchupFromContent(text) {
  if (!text) return null
  const clean = text.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '').replace(/\*\*/g, '').replace(/—.*/g, '').trim()
  const m = clean.match(/(.+?)\s*[@]\s*(.+)/)
  if (m) return { awayText: m[1].trim(), homeText: m[2].trim() }
  return null
}

function matchupKey(awayText, homeText) {
  const normalize = (s) => s.toLowerCase().replace(/[^a-z]/g, '')
  return `${normalize(awayText)}@${normalize(homeText)}`
}

function formatDateLabel(date) {
  if (!date) return 'Unknown Date'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  const diff = (d - today) / 86400000
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  if (diff === -1) return 'Yesterday'
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
}

const UMP_LABELS = {
  HP: 'Home Plate',
  '1B': 'First Base',
  '2B': 'Second Base',
  '3B': 'Third Base',
}

const UMP_COLORS = {
  HP: '#E74C3C',
  '1B': '#3498DB',
  '2B': '#2ECC71',
  '3B': '#F39C12',
}

export default function UmpiresWidget() {
  const [games, setGames] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  // UmpScorecards enrichment. Strictly optional: null means the service was
  // unreachable or the name did not match, and the widget renders exactly as
  // it did before this feature existed. Never surfaced as an error.
  const [scorecards, setScorecards] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetchUmpireScorecards().then(res => {
      if (!cancelled) setScorecards(res)
    })
    return () => { cancelled = true }
  }, [])

  // Below this many games the average is too thin to present as settled fact.
  const LOW_SAMPLE_N = 10

  const num = v => {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }

  const fmt = (v, places) => (v === null || v === undefined ? '—' : v.toFixed(places))

  const scorecardFor = useCallback(name => {
    if (!scorecards || !name || name === 'TBD') return null
    const row = scorecards.byName.get(String(name).trim())
    if (!row) return null
    const games = num(row.n)
    return {
      games,
      accuracy: num(row.overall_accuracy_wmean),
      consistency: num(row.consistency_wmean),
      runImpact: num(row.total_run_impact_mean),
      aboveExpected: num(row.accuracy_above_x_wmean),
      lowSample: games !== null && games < LOW_SAMPLE_N,
      url: umpireProfileUrl(name, scorecards.profileUrlBase),
    }
  }, [scorecards])

  const fetchData = useCallback(async () => {
    const { data, status, message } = await fetchFromProxy('/posts', { channel: '#umpires', limit: '100' })
    if (status !== 'ok' || !data) {
      setError(message || 'Failed to load umpires')
      setLoading(false)
      return
    }

    const posts = Array.isArray(data) ? data : (data.posts || [])
    const gameMap = new Map()

    for (const post of posts) {
      // Handle graphic posts — umpire assignment images
      if (post.post_type === 'graphic' && post.image_url) {
        const titleText = post.content || post.title || ''
        const matchup = parseMatchupFromContent(titleText)
        if (!matchup) {
          // Could be "All Umpire Assignments" summary image — skip for game-level data
          continue
        }

        // Filter out non-MLB teams (preseason/exhibition)
        if (!findTeam(matchup.awayText) || !findTeam(matchup.homeText)) continue

        const key = matchupKey(matchup.awayText, matchup.homeText)
        const existing = gameMap.get(key)

        // The crew is in the graphic post's CONTENT, not in embed fields.
        // Parsing it here is what makes umpire names (and therefore the
        // home-plate scorecard) appear at all for image posts.
        const graphicCrew = parseUmpires(titleText)

        if (existing) {
          if (!existing.imageUrl || new Date(post.timestamp) > new Date(existing.imageTimestamp || 0)) {
            existing.imageUrl = imageUrl(post.image_url)
            existing.imageTimestamp = post.timestamp
          }
          if (Object.keys(graphicCrew).length && !Object.keys(existing.umpires || {}).length) {
            existing.umpires = graphicCrew
          }
        } else {
          const dateObj = parseDateFromFieldValue(titleText)
          const gameTime = parseTimeFromValue(titleText)
          const awayTeam = findTeam(matchup.awayText) || { name: matchup.awayText, abbr: matchup.awayText, logo: null }
          const homeTeam = findTeam(matchup.homeText) || { name: matchup.homeText, abbr: matchup.homeText, logo: null }
          gameMap.set(key, {
            key, awayTeam, homeTeam, umpires: graphicCrew,
            gameTime, dateObj, timestamp: post.timestamp,
            imageUrl: imageUrl(post.image_url), imageTimestamp: post.timestamp,
          })
        }
        continue
      }

      if (post.post_type !== 'text_embed' || !post.fields?.length) continue
      // Skip summary posts like "All Umpire Assignments"
      if (post.title?.includes('All Umpire')) continue

      const titleDate = parseDateFromTitle(post.title)

      for (const field of post.fields) {
        const matchup = parseMatchupFromFieldName(field.name)
        if (!matchup) continue

        // Filter out non-MLB teams (preseason/exhibition)
        const awayTeam = findTeam(matchup.awayText)
        const homeTeam = findTeam(matchup.homeText)
        if (!awayTeam || !homeTeam) continue

        const key = matchupKey(matchup.awayText, matchup.homeText)
        const existing = gameMap.get(key)
        if (existing?.umpires?.HP && new Date(existing.timestamp) > new Date(post.timestamp)) continue

        const dateObj = parseDateFromFieldValue(field.value) || titleDate
        const gameTime = parseTimeFromValue(field.value)
        const umpires = parseUmpires(field.value)

        const prevImage = existing?.imageUrl || null
        const prevImageTs = existing?.imageTimestamp || null

        gameMap.set(key, {
          key, awayTeam, homeTeam, umpires,
          gameTime, dateObj, timestamp: post.timestamp,
          imageUrl: prevImage, imageTimestamp: prevImageTs,
        })
      }
    }

    // Group by date
    const grouped = {}
    for (const game of gameMap.values()) {
      const dateKey = game.dateObj ? game.dateObj.toISOString().slice(0, 10) : 'unknown'
      if (!grouped[dateKey]) grouped[dateKey] = []
      grouped[dateKey].push(game)
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const todayStr = today.toISOString().slice(0, 10)

    const sortedDates = Object.keys(grouped).sort((a, b) => {
      if (a === todayStr) return -1
      if (b === todayStr) return 1
      return new Date(a) - new Date(b)
    })

    const result = sortedDates.map(dateKey => ({
      dateKey,
      dateLabel: formatDateLabel(dateKey === 'unknown' ? null : new Date(dateKey + 'T12:00:00')),
      games: grouped[dateKey].sort((a, b) => {
        if (!a.gameTime && !b.gameTime) return 0
        if (!a.gameTime) return 1
        if (!b.gameTime) return -1
        return a.gameTime.localeCompare(b.gameTime)
      }),
    }))

    setGames(result)
    setError(null)
    setLoading(false)
    setLastUpdated(new Date())
  }, [])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 60000)
    return () => clearInterval(interval)
  }, [fetchData])

  const totalGames = games.reduce((sum, g) => sum + g.games.length, 0)

  return (
    <div className="uw-widget">
      <div className="uw-header">
        <div className="uw-header-left">
          <Scale size={18} className="uw-header-icon" />
          <h2 className="uw-title">Umpire Crews</h2>
          <span className="uw-count">{totalGames} game{totalGames !== 1 ? 's' : ''}</span>
        </div>
        <div className="uw-header-right">
          {lastUpdated && (
            <span className="uw-last-updated">{lastUpdated.toLocaleTimeString()}</span>
          )}
          <button className="uw-refresh" onClick={fetchData} title="Refresh">
            <RefreshCw size={14} className={loading ? 'uw-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="uw-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {loading && !games.length ? (
        <div className="uw-loading">Loading umpire data...</div>
      ) : (
        <div className="uw-body">
          {games.length === 0 && !loading && (
            <div className="uw-empty">No umpire data available</div>
          )}

          {games.map(group => (
            <div key={group.dateKey} className="uw-date-group">
              <div className="uw-date-header">
                <span className="uw-date-label">{group.dateLabel}</span>
                <span className="uw-date-count">{group.games.length} game{group.games.length !== 1 ? 's' : ''}</span>
              </div>

              <div className="uw-games-grid">
                {group.games.map(game => (
                  <div key={game.key} className="uw-game-card">
                    <div className="uw-matchup-bar">
                      <div className="uw-team-info">
                        {game.awayTeam?.logo && <img src={game.awayTeam.logo} alt="" className="uw-team-logo" />}
                        <span className="uw-team-name">{game.awayTeam?.short || game.awayTeam?.abbr || game.awayTeam?.name}</span>
                      </div>
                      <div className="uw-matchup-meta">
                        <span className="uw-at">@</span>
                        {game.gameTime && <span className="uw-game-time">{game.gameTime}</span>}
                      </div>
                      <div className="uw-team-info uw-team-right">
                        <span className="uw-team-name">{game.homeTeam?.short || game.homeTeam?.abbr || game.homeTeam?.name}</span>
                        {game.homeTeam?.logo && <img src={game.homeTeam.logo} alt="" className="uw-team-logo" />}
                      </div>
                    </div>

                    {/* Umpire assignment image from Discord */}
                    {game.imageUrl && (
                      <div className="uw-image-container">
                        <img
                          src={game.imageUrl}
                          alt="Umpire assignments"
                          className="uw-crew-image"
                          loading="lazy"
                          onError={(e) => { e.target.style.display = 'none' }}
                        />
                      </div>
                    )}

                    {Object.keys(game.umpires).length > 0 && (
                      <div className="uw-crew-grid">
                        {['HP', '1B', '2B', '3B'].map(pos => (
                          <div key={pos} className="uw-ump-cell">
                            <span className="uw-ump-pos" style={{ color: UMP_COLORS[pos] }}>{pos}</span>
                            <span className="uw-ump-label">{UMP_LABELS[pos]}</span>
                            <span className="uw-ump-name">{game.umpires[pos] || 'TBD'}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Home-plate umpire performance. Rendered ONLY when the
                        name matched a real UmpScorecards row — never a
                        placeholder, never a guess. */}
                    {(() => {
                      const sc = scorecardFor(game.umpires?.HP)
                      if (!sc) return null
                      return (
                        <div className="uw-scorecard">
                          <div className="uw-sc-head">
                            <span className="uw-sc-title">HOME PLATE · CAREER</span>
                            <span className={`uw-sc-sample${sc.lowSample ? ' uw-sc-low' : ''}`}>
                              {sc.games} game{sc.games === 1 ? '' : 's'}
                              {sc.lowSample ? ' · small sample' : ''}
                            </span>
                          </div>
                          <div className="uw-sc-metrics">
                            <div className="uw-sc-metric">
                              <span className="uw-sc-value">{fmt(sc.accuracy, 1)}%</span>
                              <span className="uw-sc-label">Accuracy</span>
                            </div>
                            <div className="uw-sc-metric">
                              <span className="uw-sc-value">{fmt(sc.consistency, 1)}%</span>
                              <span className="uw-sc-label">Consistency</span>
                            </div>
                            <div className="uw-sc-metric">
                              <span className="uw-sc-value">{fmt(sc.runImpact, 2)}</span>
                              <span className="uw-sc-label">Run Impact</span>
                            </div>
                          </div>
                          {sc.aboveExpected !== null && (
                            <div
                              className="uw-sc-aae"
                              style={{ color: sc.aboveExpected >= 0 ? '#4ade80' : '#f87171' }}
                            >
                              {sc.aboveExpected >= 0 ? '+' : ''}
                              {fmt(sc.aboveExpected, 2)} vs expected
                            </div>
                          )}
                          <a
                            className="uw-sc-link"
                            href={sc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {game.umpires.HP}&rsquo;s full scorecard &rarr;
                          </a>
                        </div>
                      )
                    })()}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
